// src/contexts/AuthContext.js
import React, { createContext, useState, useContext, useEffect } from "react";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if user is logged in on initial load
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error("Error parsing user data:", error);
        localStorage.removeItem("user");
      }
    }
    setLoading(false);
  }, []);

  // Login function
  const login = async (email, password) => {
    // Simplified login - in real app, this would call an API
    // For demo purposes, we'll just create a fake user
    const fakeUser = {
      id: "12345",
      email: email,
      name: email.split("@")[0],
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(email.split("@")[0])}&background=random`,
      favorites: []
    };
    
    setUser(fakeUser);
    localStorage.setItem("user", JSON.stringify(fakeUser));
    return fakeUser;
  };

  // Register function
  const register = async (email, password, name) => {
    // Simplified registration
    const newUser = {
      id: Date.now().toString(),
      email: email,
      name: name,
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=random`,
      favorites: [],
      createdAt: new Date().toISOString()
    };
    
    setUser(newUser);
    localStorage.setItem("user", JSON.stringify(newUser));
    return newUser;
  };

  // Logout function
  const logout = () => {
    setUser(null);
    localStorage.removeItem("user");
  };

  // Update user function
  const updateUser = (updatedData) => {
    const updatedUser = { ...user, ...updatedData };
    setUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
    return updatedUser;
  };

  // Add to favorites
  const addToFavorites = (gameId) => {
    if (!user) return false;
    
    const updatedUser = {
      ...user,
      favorites: [...(user.favorites || []), gameId]
    };
    
    setUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
    return true;
  };

  // Remove from favorites
  const removeFromFavorites = (gameId) => {
    if (!user) return false;
    
    const updatedUser = {
      ...user,
      favorites: (user.favorites || []).filter(id => id !== gameId)
    };
    
    setUser(updatedUser);
    localStorage.setItem("user", JSON.stringify(updatedUser));
    return true;
  };

  // Check if game is in favorites
  const isFavorite = (gameId) => {
    if (!user) return false;
    return (user.favorites || []).includes(gameId);
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    addToFavorites,
    removeFromFavorites,
    isFavorite
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};